// src/components/TableCell/TableCell.tsx
import React, { useEffect, useRef } from "react";
import { getFontAwesomeIcon } from "../../../../../utils";
import { ToggleProps, MockTableCellProps } from "../types";
import { ShowMoreWrapper } from "./tableActions/ShowMore";

const DefaultCollapsedToggle: React.FC<ToggleProps> = ({
	onExpandCell,
	truncated,
	className = "",
}) => (
	<div
		className={`flex items-center ${className}`}
		onClick={(e) => {
			e.stopPropagation();
			onExpandCell();
		}}
	>
		<span>{truncated}…</span>
	</div>
);

const MockTableCell = ({
	cell,
	isLastCell,
	isCellExpanded,
	hasCellExpansion = false,
	onExpandCell,
	maxCharacters = 30,
	cellMaxWidthWhenExpanded = 50,
	additionalCellClassNames = "",
	toggleComponent: ToggleComponent = DefaultCollapsedToggle,
	linkTextClassNames = "text-purple-500 ml-1 underline",
	cellTextClassNames = "",
	onCellOverflow = () => {},
	expandedCellAlwaysVisibleActionButton,
	expandedCellActionButton,
	hoverIconStyles,
	showExpandRowChevronButton,
	isTableRowExpanded,
	cellIndex,
	isRightSideStickyColumn,
	leftSideStickyColumnBackgroundCellColor = "bg-navy-50",
}: MockTableCellProps) => {
	const prev = useRef<boolean>();

	// --- truncation logic ---
	const isString = typeof cell.value === "string";
	const fullText = isString ? (cell.value as string) : "";
	const isLong = isString && fullText.length >= maxCharacters;
	const truncated = fullText.slice(0, maxCharacters);
	const showCellExpansionToggle = hasCellExpansion && isLong;

	const isLeftSideStickyColumn = cell?.column?.sticky === "left";
	const isLeftSideLastStickyColumn =
		isLeftSideStickyColumn && cellIndex === 1;

	//// CLASSNAMES ////
	const baseClassNames = `font-light text-sm text-left ${
		isRightSideStickyColumn ? "pl-5" : "px-5"
	}`;

	const isStickyLeftSideClasses = `${
		isLeftSideStickyColumn && leftSideStickyColumnBackgroundCellColor
	}`;
	const cellContentWrapperClasses = `min-h-[40px]  flex select-none  ${
		showCellExpansionToggle ? "cursor-pointer" : ""
	}`;

	const showExpandRowChevronButtonClasses = `size-[18px]
				inline-flex items-center justify-center
				cursor-pointer p-1 rounded-[2px] text-xs
				border-1 border-navy-200
				${
					isTableRowExpanded
						? "bg-blue-600 text-white border-navy-200 row-hover:bg-[#677FD5] row-hover:text-white"
						: "row-hover:border-primary row-hover:bg-supply-blue-50 row-hover:text-primary"
				}`;

	// extract key before spreading
	const rawCellProps = cell.getCellProps({
		className: isLastCell ? "" : baseClassNames,
		style: isLeftSideStickyColumn
			? {
					position: "sticky",
					left: (cell.column.style as any)?.left || 0,
					zIndex: 10,
				}
			: {},
	}) as any;
	const { key: cellKey, ...safeCellProps } = rawCellProps;

	useEffect(() => {
		if (prev.current !== showCellExpansionToggle) {
			onCellOverflow(showCellExpansionToggle);
			prev.current = showCellExpansionToggle;
		}
	}, [showCellExpansionToggle, onCellOverflow]);

	const iconName = isTableRowExpanded ? "chevronUp" : "chevronDown";

	return (
		<td
			key={cellKey}
			{...safeCellProps}
			className={`${baseClassNames} ${isStickyLeftSideClasses}  ${
				showExpandRowChevronButton && "w-96"
			}`}
		>
			<div
				className={`${cellContentWrapperClasses} group`}
				style={{ maxWidth: `${cellMaxWidthWhenExpanded}ch` }}
			>
				{showExpandRowChevronButton && (
					<div className={`flex items-center pr-5`}>
						<span className={showExpandRowChevronButtonClasses}>
							{getFontAwesomeIcon(iconName, "regular")}
						</span>
					</div>
				)}
				{showCellExpansionToggle ? (
					<ShowMoreWrapper
						isExpanded={isCellExpanded}
						onToggle={onExpandCell}
						needsExpansion={true}
					>
						{isCellExpanded ? (
							<div
								className="relative flex flex-col min-w-3/4 group"
								style={{
									maxWidth: `${cellMaxWidthWhenExpanded}ch`,
								}}
							>
								{/* 1) the text */}
								<span
									className={`text-left pr-5 ${cellTextClassNames}`}
								>
									{fullText}
								</span>

								{/* 2) always-visible action */}
								{typeof expandedCellAlwaysVisibleActionButton ===
									"function" && (
									<div className="absolute top-2 right-2">
										{expandedCellAlwaysVisibleActionButton(
											fullText,
											cell.row.index,
										)}
									</div>
								)}

								{/* hover-only action */}
								{typeof expandedCellActionButton ===
									"function" && (
									<div
										className={`
										mt-1
										opacity-0 group-hover:opacity-100
										transition-opacity
										bg-transparent
										${hoverIconStyles}
										`}
									>
										{expandedCellActionButton(
											fullText,
											cell.row.index,
										)}
									</div>
								)}
							</div>
						) : (
							<ToggleComponent
								expanded={isCellExpanded}
								onExpandCell={onExpandCell}
								truncated={truncated}
								fullText={fullText}
								className={additionalCellClassNames}
								linkTextClassNames={linkTextClassNames}
							/>
						)}
					</ShowMoreWrapper>
				) : (
					cell.render("Cell")
				)}
			</div>
			{isLeftSideLastStickyColumn && (
				<div className="w-[1px] h-full bg-gray-300 absolute right-0 top-0"></div>
			)}
		</td>
	);
};

export default MockTableCell;
